import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import { Alert } from '../../ui/Alert';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { humanizeEnum } from '../../../utils/humanize';

// IDI-737: Send creates each login via add_user (parent's onConfirm returns
// per-person results). One-time passwords live only in this dialog's state.
export function SendInvitesModal({ open, onClose, onConfirm, invitees = [] }) {
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState({});

  const handleSend = async () => {
    setSending(true);
    try {
      const snapshot = [...invitees];
      const res = await onConfirm(snapshot);
      setResults(Array.isArray(res) ? res : []);
    } catch (err) {
      // Error handled by parent
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResults(null);
    setCopied({});
    onClose();
  };

  const handleCopy = async (idx, password) => {
    // IDI-744 D2: "Copied" only once the write succeeds; no clipboard or a refusal stays "Copy".
    try {
      await navigator.clipboard.writeText(password);
      setCopied(prev => ({ ...prev, [idx]: true }));
    } catch (err) { /* clipboard unavailable or refused */ }
  };

  // IDI-744 D1: nothing closes the dialog while Send runs (the × stays, inert).
  const guardedClose = () => { if (!sending) handleClose(); };

  if (results) {
    return (
      <Dialog open={open} onClose={handleClose} size="md">
        <DialogHeader onClose={handleClose}>Send Invitations</DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            {results.map((r, idx) => (
              <div key={idx} className="card px-4 py-3 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {r.person.firstName} {r.person.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{r.person.email}</p>
                </div>
                {r.ok ? (
                  <>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono select-all">
                        {r.temporaryPassword}
                      </code>
                      <Button type="button" variant="secondary" size="sm" onClick={() => handleCopy(idx, r.temporaryPassword)}>
                        {copied[idx] ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    {r.emailStatus === 'sent' && (
                      <Alert variant="info">An invite email was also sent to {r.email}.</Alert>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-red-600">{r.error}</p>
                )}
              </div>
            ))}
            <Alert variant="warning">
              This is a one-time login password. The user will be asked to set their own password on first sign-in. Share it securely.
            </Alert>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={handleClose}>Done</Button>
        </DialogFooter>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={guardedClose} size="md">
      <DialogHeader onClose={guardedClose}>Send Invitations</DialogHeader>
      <DialogBody>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <EnvelopeIcon className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              Send {invitees.length} invitation{invitees.length !== 1 ? 's' : ''}
            </p>
            <p className="text-sm text-gray-500">
              Each person gets a login with a one-time password.
            </p>
          </div>
        </div>

        <div className="card divide-y divide-gray-100 max-h-60 overflow-y-auto">
          {invitees.map((person, idx) => (
            <div key={idx} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {person.firstName} {person.lastName}
                </p>
                <p className="text-sm text-gray-500">{person.email}</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 capitalize">
                {humanizeEnum(person.role) || 'Team Member'}
              </span>
            </div>
          ))}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={guardedClose} disabled={sending}>Cancel</Button>
        <Button onClick={handleSend} loading={sending}>
          Send Invitations
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
